import { Injectable } from "@angular/core";
import {
  Actions,
  createEffect,
  CreateEffectMetadata,
  ofType,
} from "@ngrx/effects";
import { Action } from "@ngrx/store";
import { Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { SkillsCheckTabActionTypes } from "src/app/constants/StoreConstants";
import { SkillsCheck, SkillsCheckPanel } from "src/app/_models/SkillsCheck";
import { SkillsCheckService } from "src/app/_services/skills-check.service";
import {
  GetSkillsCheckResultByLearnerId,
  GetSkillsCheckResultError,
  GetSkillsCheckResultSuccess,
  IsSkillsCheckEnabledByLearnerId,
  IsSkillsCheckEnabledError,
  IsSkillsCheckEnabledSuccess,
  UpdateAttemptPanelStateSuccess,
  UpdatePanelStateSuccess,
  UpdateSkillsCheckTabState,
} from "./skills-check-tab.actions";

@Injectable()
export class SkillsCheckTabEffects {
  constructor(
    private action$: Actions,
    private skillsCheckService: SkillsCheckService
  ) {}

  public isEnabledSkillsCheck$: Observable<Action> & CreateEffectMetadata =
    createEffect((): Observable<Action> => {
      return this.action$.pipe(
        ofType(SkillsCheckTabActionTypes.IsSkillsCheckEnabled),
        switchMap((payload: { courseId: number }) =>
          this.skillsCheckService
            .getIsSkillsCheckEnabled$(payload.courseId)
            .pipe(
              map((isEnabled: boolean) => {
                return IsSkillsCheckEnabledSuccess({ isEnabled: isEnabled });
              }),
              catchError((error) => {
                return of(IsSkillsCheckEnabledError({ error: error }));
              })
            )
        )
      );
    });

  public isEnabledSkillsCheckByLearnerIdEffect$ = createEffect(() => 
    this.action$.pipe(
      ofType(IsSkillsCheckEnabledByLearnerId),
      switchMap((payload) => 
        this.skillsCheckService.getIsSkillsCheckEnabledByLearnerId$(payload.learnerId, payload.courseId).pipe(
          map((result: boolean) => {
            return IsSkillsCheckEnabledSuccess({isEnabled: result});
          }),
          catchError((error) => {
            return of(IsSkillsCheckEnabledError({error: error}));
          })
        )
      )
    )
  )

  public getSkillsCheckResult$: Observable<Action> & CreateEffectMetadata =
    createEffect((): Observable<Action> => {
      return this.action$.pipe(
        ofType(SkillsCheckTabActionTypes.GetSkillsCheckResult),
        switchMap((payload: { courseId: number }) =>
          this.skillsCheckService.getLearnerSkillsCheck$(payload.courseId).pipe(
            map((result: SkillsCheck[]) => {
              var scPanel: SkillsCheckPanel[] = Object.keys(result).map(
                (name) => ({
                  name,
                  attempt: result[name],
                })
              );
              return GetSkillsCheckResultSuccess({
                panel: scPanel,
              });
            }),
            catchError((error) => {
              return of(GetSkillsCheckResultError({ error: error }));
            })
          )
        )
      );
    });

  public getSkillsCheckResultByLearnerId$: Observable<Action> & CreateEffectMetadata =
    createEffect((): Observable<Action> => {
      return this.action$.pipe(
        ofType(GetSkillsCheckResultByLearnerId),
        switchMap((payload: { learnerId: number, courseId: number }) =>
          this.skillsCheckService.getLearnerSkillsCheckByLearnerId$(payload.learnerId, payload.courseId).pipe(
            map((result: SkillsCheck[]) => {
              var scPanel: SkillsCheckPanel[] = Object.keys(result).map(
                (name) => ({
                  name,
                  attempt: result[name],
                })
              );
              return GetSkillsCheckResultSuccess({
                panel: scPanel,
              });
            }),
            catchError((error) => {
              return of(GetSkillsCheckResultError({ error: error }));
            })
          )
        )
      );
    });

  public updatePanelState$: Observable<Action> & CreateEffectMetadata =
    createEffect((): Observable<Action> => {
      return this.action$.pipe(
        ofType(SkillsCheckTabActionTypes.UpdatePanelState),
        map(() => {
          return UpdatePanelStateSuccess();
        })
      );
    });

  public updateAttemptPanelState$: Observable<Action> & CreateEffectMetadata =
    createEffect((): Observable<Action> => {
      return this.action$.pipe(
        ofType(SkillsCheckTabActionTypes.UpdateAttemptPanelState),
        map(() => {
          return UpdateAttemptPanelStateSuccess();
        })
      );
    });

  public updateSkillsCheckTabState$: Observable<Action> & CreateEffectMetadata =
    createEffect((): Observable<Action> => {
      return this.action$.pipe(
        ofType(SkillsCheckTabActionTypes.GetSkillsCheckResultSuccess),
        map(() => {
          return UpdateSkillsCheckTabState();
        })
      );
    });
}
